import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Product } from '@/data/products';

// Single shared product-card presentation used by both Shop and Home
// (Cult Shelf / Chosen for Your Skin) — previously each page had its own
// near-duplicate implementation that had drifted apart (different image
// fallback, badge shapes, title font, CTA style).
interface ShopStyleProductCardProps {
  product: Product;
  index: number;
  isVisible: boolean;
  addedToCart: boolean;
  onAddToCart: () => void;
  categoryColor: string;
  compact?: boolean;
  forceBadge?: string;
}

export function ShopStyleProductCard({
  product,
  index,
  isVisible,
  addedToCart,
  onAddToCart,
  categoryColor,
  compact,
  forceBadge,
}: ShopStyleProductCardProps) {
  return (
    <div
      className={cn(
        'group bg-white rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-xl',
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      )}
      style={{
        transitionDelay: `${index * 50}ms`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      }}
    >
      {/* Image */}
      <Link to={`/product/${product.id}`} className="block relative overflow-hidden">
        <div className={cn('relative bg-[#F7F5EE]', compact ? 'aspect-[4/3]' : 'aspect-square')}>
          <img
            src={product.images[0]}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
              (e.target as HTMLImageElement).parentElement!.innerHTML = `
                <div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#567D38] to-[#1a3d2a]">
                  <div class="text-center p-4">
                    <div class="w-16 h-16 mx-auto mb-2 bg-[#BDD630] rounded-full flex items-center justify-center">
                      <span class="text-2xl">🌿</span>
                    </div>
                    <p class="text-white/80 text-sm font-medium">${product.name}</p>
                  </div>
                </div>
              `;
            }}
          />

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {forceBadge ? (
              <span
                className="px-3 py-1 text-xs font-bold rounded-full text-white"
                style={{ background: '#E07A5F' }}
              >
                {forceBadge}
              </span>
            ) : (
              <>
                {product.isBestseller && (
                  <span className="px-3 py-1 text-xs font-bold rounded-full text-white" style={{ background: '#E07A5F' }}>
                    CULT FAVOURITE
                  </span>
                )}
                {product.isNew && (
                  <span className="px-3 py-1 text-xs font-bold rounded-full" style={{ background: '#BDD630', color: '#567D38' }}>
                    NEW
                  </span>
                )}
                {product.originalPrice && (
                  <span className="px-3 py-1 text-xs font-bold rounded-full text-white" style={{ background: '#567D38' }}>
                    SALE
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </Link>

      {/* Content */}
      <div className="p-5">
        {/* Category */}
        <span className="text-xs font-semibold uppercase tracking-wider mb-2 block" style={{ color: categoryColor }}>
          {product.subcategory || product.category}
        </span>

        {/* Title */}
        <Link to={`/product/${product.id}`}>
          <h3 className="font-serif text-lg font-semibold mb-2 group-hover:underline transition-colors" style={{ color: '#1A2E12' }}>
            {product.name}
          </h3>
        </Link>

        {/* Rating */}
        {product.reviews > 0 && (
          <div className="flex items-center gap-1 mb-3">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={cn('w-4 h-4', i < Math.floor(product.rating) ? 'fill-current' : '')}
                style={{ color: i < Math.floor(product.rating) ? '#F4C95D' : '#D9B38C' }}
              />
            ))}
            <span className="text-xs ml-1" style={{ color: '#6B7B6E' }}>({product.reviews})</span>
          </div>
        )}

        {/* Price & Add to Cart */}
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-bold" style={{ color: '#1A2E12' }}>R{product.price.toFixed(2)}</span>
            {product.originalPrice && (
              <span className="text-sm line-through" style={{ color: '#6B7B6E' }}>R{product.originalPrice.toFixed(2)}</span>
            )}
          </div>

          <button
            onClick={(e) => { e.preventDefault(); onAddToCart(); }}
            disabled={addedToCart || !product.inStock}
            aria-label={
              addedToCart ? `${product.name} added to cart` : product.inStock ? `Add ${product.name} to cart` : `${product.name} out of stock`
            }
            className={cn(
              'px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300',
              !addedToCart && 'hover:scale-105',
              !product.inStock && 'opacity-40 cursor-not-allowed'
            )}
            style={{
              backgroundColor: addedToCart ? '#567D38' : '#BDD630',
              color: addedToCart ? 'white' : '#567D38',
            }}
          >
            {addedToCart ? 'Added!' : product.inStock ? 'Add to My Ritual' : 'Out of Stock'}
          </button>
        </div>

        {/* Key Ingredients */}
        {!compact && product.keyIngredients.length > 0 && (
          <div className="mt-3 pt-3 border-t" style={{ borderColor: '#e8e2d9' }}>
            <p className="text-xs" style={{ color: '#6B7B6E' }}>
              Key: {product.keyIngredients.slice(0, 3).join(', ')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ShopStyleProductCard;
